import React from 'react';

export const MAX_KNOWLEDGE_BASE_FILES = 3;
export const KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];
export const KNOWLEDGE_BASE_ACCEPT =
  '.pdf,.docx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/csv';

export const getFileExtension = (fileName = '') => {
  const match = String(fileName).toLowerCase().match(/\.[^.]+$/);
  return match ? match[0] : '';
};

export const isAcceptedKnowledgeBaseFile = (file) =>
  KNOWLEDGE_BASE_ACCEPTED_EXTENSIONS.includes(getFileExtension(file?.name));

export const useProjectKnowledgeBaseFiles = (t = (key) => key) => {
  const [isKnowledgeBaseSectionOpen, setIsKnowledgeBaseSectionOpen] = React.useState(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = React.useState([]);
  const [knowledgeBaseFileError, setKnowledgeBaseFileError] = React.useState('');

  const handleKnowledgeBaseFileChange = (e) => {
    const input = e.target;
    const files = Array.from(input?.files || []);
    if (input && typeof input.value === 'string') {
      input.value = '';
    }
    if (!files.length) return;

    const validFiles = files.filter(isAcceptedKnowledgeBaseFile);
    if (validFiles.length !== files.length) {
      setKnowledgeBaseFileError(
        t('Format de fichier non supporté. Formats acceptés : PDF, DOCX, TXT, MD, CSV.')
      );
    }

    if (!validFiles.length) return;

    const remaining = MAX_KNOWLEDGE_BASE_FILES - knowledgeBaseFiles.length;

    if (remaining <= 0 || validFiles.length > remaining) {
      setKnowledgeBaseFileError(t('Vous pouvez importer 3 fichiers maximum.'));
    } else if (validFiles.length === files.length) {
      setKnowledgeBaseFileError('');
    }

    if (remaining <= 0) return;

    const acceptedFiles = validFiles.slice(0, remaining);
    setKnowledgeBaseFiles((prev) => [...prev, ...acceptedFiles]);
  };

  const handleKnowledgeBaseFileDrop = (e) => {
    e.preventDefault();
    if (!e.dataTransfer?.files?.length) return;
    handleKnowledgeBaseFileChange({ target: { files: e.dataTransfer.files } });
  };

  const handleRemoveKnowledgeBaseFile = (indexToRemove) => {
    setKnowledgeBaseFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setKnowledgeBaseFileError('');
  };

  const resetKnowledgeBaseFiles = () => {
    setIsKnowledgeBaseSectionOpen(false);
    setKnowledgeBaseFiles([]);
    setKnowledgeBaseFileError('');
  };

  const isKnowledgeBaseLimitReached = knowledgeBaseFiles.length >= MAX_KNOWLEDGE_BASE_FILES;

  return {
    isKnowledgeBaseSectionOpen,
    setIsKnowledgeBaseSectionOpen,
    knowledgeBaseFiles,
    knowledgeBaseFileError,
    isKnowledgeBaseLimitReached,
    handleKnowledgeBaseFileChange,
    handleKnowledgeBaseFileDrop,
    handleRemoveKnowledgeBaseFile,
    resetKnowledgeBaseFiles,
  };
};

const ProjectKnowledgeBaseSection = ({
  t = (key) => key,
  inputId = 'project-kb-upload-input',
  isOpen,
  onToggle,
  files = [],
  fileError = '',
  isLimitReached = false,
  disabled = false,
  onFileChange,
  onFileDrop,
  onRemoveFile,
  sectionTitle,
}) => (
  <div className="redaction-kb-section">
    <h6
      className="redaction-kb-section-title"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img
          src="/Images/conai-icon.png"
          alt="info"
          className="redaction-kb-section-icon"
        />
        {sectionTitle || t('Section 2 — Base de connaissance (optionnel)')}
      </span>
      <span
        style={{
          display: 'inline-block',
          transition: 'transform 0.2s ease',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          fontSize: 14,
          color: '#818181',
        }}
      >
        ▼
      </span>
    </h6>

    {isOpen && (
      <>
        <p className="redaction-kb-section-description">
          {t("Optionnel : vos ressources internes aident l’agent à produire des contenus plus précis et personnalisés.")}
        </p>
        <div
          className={`redaction-kb-upload-dropzone${isLimitReached ? ' is-limit-reached' : ''}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onFileDrop}
        >
          <input
            type="file"
            id={inputId}
            multiple
            accept={KNOWLEDGE_BASE_ACCEPT}
            disabled={isLimitReached || disabled}
            style={{ display: 'none' }}
            onChange={onFileChange}
          />
          <div className="redaction-kb-upload-dropzone-inner">
            <div className="redaction-kb-upload-dropzone-icon">
              <img src="/Images/imp-icon.png" alt="Upload" />
            </div>
            <div
              className={`redaction-kb-upload-dropzone-text-main${isLimitReached ? ' is-limit-message' : ''}`}
            >
              {isLimitReached
                ? t('Vous avez atteint le nombre maximum de fichiers autorisés pour cet agent.')
                : t('Glissez-déposez votre fichier')}
            </div>
            {!isLimitReached && (
              <div className="redaction-kb-upload-dropzone-text-sub">
                {t('ou cliquez pour parcourir')}
                {' — '}
                {t('Formats acceptés : PDF, DOCX, TXT, MD, CSV.')}
              </div>
            )}
            <label
              htmlFor={isLimitReached ? undefined : inputId}
              className={`redaction-kb-upload-dropzone-button${isLimitReached ? ' is-disabled' : ''}`}
            >
              {t('Parcourir les fichiers')}
            </label>
            {files.length > 0 && (
              <div className="redaction-kb-upload-dropzone-filelist">
                {files.map((file, idx) => (
                  <div
                    key={`${file.name}-${idx}`}
                    className="redaction-kb-upload-dropzone-filename-row"
                  >
                    <span
                      className="redaction-kb-upload-dropzone-filename"
                      title={file.name}
                    >
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFile(idx);
                      }}
                      className="redaction-kb-upload-dropzone-remove"
                      aria-label={t('Supprimer le fichier')}
                    >
                      <img
                        src="/Images/close-iconnn.png"
                        alt={t('Supprimer')}
                        style={{ width: 14, height: 14 }}
                      />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {fileError ? (
          <div className="redaction-kb-upload-dropzone-error">{fileError}</div>
        ) : null}
      </>
    )}
  </div>
);

export default ProjectKnowledgeBaseSection;
